import { useCallback, useState } from 'react';
import { Color, Size, t } from '@bms2/shared';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Button, DatePicker, TextInput } from '../../components/controls';
import { Modal } from '../../components/modals/Modal';
import modalStyles from '../../components/modals/modal.module.css';
import { PAGES_PATH, patch, post } from './api';
import { useDocumentToday } from '../../shell/ViewerZone';
import styles from './RecordPage.module.css';

/**
 * The occupancy a pencil opened, on the Company page's Sites tile or the
 * Location page's Occupants tile (decision 0589). 🔨 One modal for both,
 * because both tiles draw one renderer over one record: a site is the occupancy
 * of a building by a company (decision 0043), and the two tiles are the two
 * ends of it.
 *
 * 🔨 The building itself is not edited here. An address belongs to the
 * `LocationRecord`, which the Location page edits, and a site that moved to
 * another building is a new occupancy rather than a corrected one.
 */
export interface SiteOccupancyEdit {
  siteId: string;
  /** What the row is titled by, for the modal's subtitle. */
  label: string;
  name: string;
  suite: string;
  /** The occupancy's own first day, as a day. */
  startDate: string;
  /** Its last day where it has ended, and null while the company still occupies the building. */
  endDate: string | null;
}

export interface SiteOccupancyModalProps {
  open: boolean;
  editing: SiteOccupancyEdit | null;
  apiBaseUrl?: string;
  onCancel: () => void;
  onSaved: () => void;
  openerId?: string | null;
}

/** One occupancy's own route, which the correction patches and the end posts under (decision 0589). */
export const sitePath = (siteId: string) => `${PAGES_PATH}/sites/${siteId}`;

export function SiteOccupancyModal({ open, editing, apiBaseUrl = '', onCancel, onSaved, openerId = null }: SiteOccupancyModalProps) {
  // 🔨 The last day opens on the viewer's own day, read once through
  // useDocumentToday, never the machine's UTC calendar day (decision 0261; T529).
  const today = useDocumentToday();
  const [name, setName] = useState(editing?.name ?? '');
  const [suite, setSuite] = useState(editing?.suite ?? '');
  const [startDate, setStartDate] = useState(editing?.startDate ?? '');
  /**
   * 🔨 The last day is the one field with two readings, and which one it has is
   * the row's state rather than a mode the person picks: on an occupancy already
   * ended it is a correction Save writes, and on an open one it is the day End
   * occupancy closes it on.
   */
  const [lastDay, setLastDay] = useState(editing?.endDate ?? today);
  const [error, setError] = useState<string | null>(null);
  const alreadyEnded = editing?.endDate !== null && editing?.endDate !== undefined;

  const save = useCallback(async () => {
    if (!editing) return;
    setError(null);
    const answer = await patch(
      sitePath(editing.siteId),
      {
        name,
        suite,
        ...(startDate ? { startDate } : {}),
        // 🔨 An open occupancy's end is End occupancy's own act, so Save never sets one.
        ...(alreadyEnded && lastDay ? { endDate: lastDay } : {}),
      },
      apiBaseUrl,
    );
    if (!answer.ok) {
      setError(answer.message);
      return;
    }
    onSaved();
    onCancel();
  }, [alreadyEnded, apiBaseUrl, editing, lastDay, name, onCancel, onSaved, startDate, suite]);

  /**
   * End occupancy, from inside the edit (decision 0589): the last day above
   * through the occupancy's one end path. The confirmation the Destructive button
   * opens says what closing does and that reoccupying is a new site, which the
   * row's own End — ending today, silently — could never say.
   */
  const end = useCallback(async () => {
    if (!editing) return;
    setError(null);
    const answer = await post(`${sitePath(editing.siteId)}/end`, { endDate: lastDay }, apiBaseUrl);
    if (!answer.ok) {
      setError(answer.message);
      return;
    }
    onSaved();
    onCancel();
  }, [apiBaseUrl, editing, lastDay, onCancel, onSaved]);

  return (
    <Modal
      open={open}
      title={t('occupancyModal.title')}
      subtitle={editing?.label ?? t('occupancyModal.subtitle')}
      onCancel={onCancel}
      openerId={openerId}
      dataModal="edit-occupancy"
      actions={[
        { kind: 'primary', label: t('modal.save'), onAct: () => void save(), disabled: startDate === '' },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      <div data-occupancy-modal="true" data-occupancy-state={alreadyEnded ? 'ended' : 'open'}>
        {open && error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
        {/* 🔨 The stored suite is never normalised: what the person typed is what the document prints. */}
        <TextInput size={Size.Medium} label={t('occupancyModal.name')} value={name} onChange={setName} />
        <TextInput size={Size.Medium} label={t('occupancyModal.suite')} value={suite} onChange={setSuite} />
        <DatePicker size={Size.Small} label={t('occupancyModal.startDate')} value={startDate} onChange={setStartDate} mode="date" name="occupancyStartDate" required />
        {alreadyEnded ? (
          <>
            <DatePicker size={Size.Small} label={t('occupancyModal.endDate')} value={lastDay} onChange={setLastDay} mode="date" name="occupancyEndDate" />
            <p className={modalStyles.readOnly} data-occupancy-end-note="true">{t('occupancyModal.endDateNote')}</p>
          </>
        ) : (
          /* 🔨 The End is in the Body, not the Footer: the Footer holds one destructive
             secondary and this one needs the day beside it to make sense (piece 5). */
          <div data-occupancy-end-block="true">
            <DatePicker size={Size.Small} label={t('occupancyModal.endLead')} value={lastDay} onChange={setLastDay} mode="date" name="occupancyLastDay" />
            <p className={modalStyles.readOnly}>{t('occupancyModal.endHint')}</p>
            <div className={styles.actions}>
              <Button
                size={Size.Small}
                color={Color.Destructive}
                text={[t('occupancyModal.end')]}
                actions={[() => void end()]}
                disabled={lastDay === ''}
                confirm={{ title: t('occupancyModal.endTitle'), consequence: { body: t('occupancyModal.endConsequence') }, confirmLabel: t('occupancyModal.endConfirm') }}
              />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
